import React from 'react';
import { EpiContent, EpiImage } from '@api/types';
import { LinkListBlock, Html, Image, ContentArea, BlockProperty } from '@components';
import { Box, Container, Grid, Skeleton, Stack, styled, ThemeProvider } from '@mui/material';
import { breakpoints, palette } from '@themes/theme.helpers';
import { withDate } from '@utils/date.utils';
import { themeDark } from '@themes/dark/theme.dark';

const FooterWrapper = styled('footer')`
  background: ${palette.background('default')};
  color: ${palette.text('primary')};
  border-top: 1px solid #ffffff;
  padding: 30px 0;
  ${breakpoints.up('md')} {
    padding: 60px 80px 48px 80px;
  }

  a {
    color: #ffffff;
  }
`;

const BrandingColumn = styled(Grid)`
  ${breakpoints.up('md')} {
    border-right: 1px solid #fff;
  }
  .footer-logo {
    margin: 0 0 20px 0;
    ${breakpoints.up('md')} {
      margin: 0 0 40px 0;
    }
  }
`;

const LinksColumn = styled(Grid)`
  padding: 20px 0;
  ${breakpoints.up('md')} {
    padding: 0 0 0 130px;
  }
  a {
    padding: 0 0 10px 0;
    font-size: 16px;
    ${breakpoints.up('md')} {
      font-size: 20px;
      padding: 0 0 20px 0;
    }
  }
`;

const StatementText = styled(Box)`
  padding: 10px 0;
  ${breakpoints.up('md')} {
    padding: 0 48px 0 0;
    font-size: 18px;
    line-height: 30px;
  }
`;

const ContactBox = styled(Box)`
  padding: 20px 0;
  border-top: 1px solid #ffffff;
  border-bottom: 1px solid #ffffff;
  ${breakpoints.up('md')} {
    padding: 80px 0 0 0;
    border: 0;
  }

  .contact-label {
    margin: 0 0 20px 0;
    display: block;
    font-weight: 700;
    ${breakpoints.up('md')} {
      display: inline-block;
      margin: 0 32px 0 0;
      font-size: 20px;
    }
  }

  .link-block {
    display: inline-block;
    border: 0;
    background: transparent;
    margin: 0;
    padding: 0;
    &:hover {
      background: transparent;
    }
    a {
      padding: 6px 16px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 32px;
      color: #ffffff;
      margin: 0 16px 0 0;
      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
      ${breakpoints.up('md')} {
        margin: 0 32px 0 0;
        font-size: 16px;
        line-height: 20px;
      }

      display: flex;
      .md-icon {
        margin-right: 8px;
      }
    }
  }
`;

const CopyrightBox = styled(Box)`
  font-size: 16px;
  padding: 10px 0 0 0;
  ${breakpoints.up('md')} {
    padding: 32px 0 0 0;
    display: flex;
  }
  .copyright-text {
    ${breakpoints.up('md')} {
      margin: 0 70px 0 0;
    }
  }
  a {
    color: ${palette.primary('main')};
    text-decoration: underline;
  }
`;

const SocialMediaBox = styled(Box)`
  display: flex;
  .link-block {
    margin: 0 16px 0 0;
  }
  .md-icon {
    background: ${palette.primary('main')};
    width: 32px;
    height: 32px;
    border-radius: 16px;
    color: #000;
    padding: 4px;
  }
  .link-text {
    display: none;
  }
`;

export interface FooterBlockProps {
  title?: string;
  children?: React.ReactNode;
  logo?: EpiImage;
  statementText?: string;
  disclaimerText?: string;
  copyrightText?: string;
  columnHeading1?: string;
  columnHeading2?: string;
  columnLinks1?: any;
  columnLinks2?: any;
  contactLabel?: string;
  contactLinks?: EpiContent;
  socialMediaLinks?: EpiContent;
}
export const FooterBlock = ({
  logo,
  columnLinks1,
  columnLinks2,
  columnHeading1,
  columnHeading2,
  statementText,
  disclaimerText,
  copyrightText,
  contactLabel,
  contactLinks,
  socialMediaLinks,
}: FooterBlockProps) => {
  return (
    <ThemeProvider theme={themeDark}>
      <FooterWrapper className='footer-block'>
        <Container>
          <Grid container>
            <BrandingColumn item xs={12} md={4}>
              <BlockProperty property='logo'>
                <Image image={logo} className='footer-logo' addWrapper />
              </BlockProperty>
              <BlockProperty property='statementText'>
                <StatementText>
                  <Html html={statementText} />
                </StatementText>
              </BlockProperty>
            </BrandingColumn>
            <LinksColumn item xs={12} sm={6} md={4}>
              <LinkListBlock
                title={columnHeading1}
                titlePropertyName='columnHeading1'
                links={columnLinks1}
                linksPropertyName='columnLinks1'
              />
            </LinksColumn>
            <LinksColumn item xs={12} sm={6} md={4}>
              <LinkListBlock
                title={columnHeading2}
                titlePropertyName='columnHeading2'
                links={columnLinks2}
                linksPropertyName='columnLinks2'
              />
              <BlockProperty property='socialMediaLinks'>
                <SocialMediaBox>
                  <ContentArea content={socialMediaLinks} />
                </SocialMediaBox>
              </BlockProperty>
            </LinksColumn>
          </Grid>
          <BlockProperty property='contactLinks'>
            <ContactBox>
              <Box className='contact-label' component='span'>
                {contactLabel}
              </Box>
              <ContentArea content={contactLinks} />
            </ContactBox>
          </BlockProperty>
          <CopyrightBox>
            <BlockProperty property='copyrightText'>
              <Box className='copyright-text'>{withDate(copyrightText)}</Box>
            </BlockProperty>
            <BlockProperty property='disclaimerText'>
              <Html html={disclaimerText} />
            </BlockProperty>
          </CopyrightBox>
        </Container>
      </FooterWrapper>
    </ThemeProvider>
  );
};

/**
 * Display Footer Area Placeholder with Skeletons
 **/
export const FooterBlockPlaceholder = () => (
  <FooterWrapper className='footer-block placeholder'>
    <Container className='fade-in-transition'>
      <Stack spacing={{ xs: 0, md: 2 }} direction='row'>
        <PlaceholderColumn />
        <PlaceholderColumn />
        <PlaceholderColumn />
        <PlaceholderColumn />
      </Stack>
    </Container>
  </FooterWrapper>
);

const PlaceholderColumn = () => (
  <Box sx={{ width: '100%' }}>
    <Skeleton width='100%' />
    <Skeleton width='100%' />
    <Skeleton width='100%' />
  </Box>
);
